import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { sites as sitesApi, panels as panelsApi, ApiError } from '../lib/api'
import { useMe } from '../lib/useMe'
import { AuthLayout, ErrorBox } from '../components/AuthLayout'
import { ConfirmDialog } from '../components/ConfirmDialog'

// First cut of multi-site navigation (plan §6c): a plain list, inline create, delete with
// a confirm step. No per-site detail/panel view yet — that's the wizard itself (§1-§7 of
// the plan), not built here. This exists so a site created by hand (or later, by the
// wizard) has somewhere to actually show up, instead of vanishing into a dashboard with no
// site-awareness at all.
export function Sites() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useMe()
  const list = useQuery({ queryKey: ['sites'], queryFn: sitesApi.list, enabled: me.isSuccess })

  const [domain, setDomain] = useState('')
  const [domainFocused, setDomainFocused] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function onCreate(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      const site = await sitesApi.create(domain)
      // Straight to the site's own page, not back onto this list — a manually-added site
      // has no panel yet, and the site-detail page is what shows "no panel yet, set one
      // up" instead of leaving a dead-end row with nothing to do next (app-shell plan §3).
      navigate(`/sites/${site.id}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de site niet toevoegen.')
      setBusy(false)
    }
  }

  async function onDelete(id: number) {
    setError('')
    try {
      await sitesApi.remove(id)
      await queryClient.invalidateQueries({ queryKey: ['sites'] })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de site niet verwijderen.')
    }
  }

  async function onRunNow(panelId: number) {
    setError('')
    setBusy(true)
    try {
      const started = await panelsApi.run(panelId)
      navigate(`/runs/${started.run_id}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kon de meting niet starten.')
      setBusy(false)
    }
  }

  if (me.isError) {
    navigate('/login')
    return null
  }

  return (
    <AuthLayout title="Sites" wide>
      <ErrorBox>{error}</ErrorBox>

      <p className="mb-4">
        <Link
          to="/onboarding"
          className="inline-block rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90"
        >
          Nieuwe site instellen →
        </Link>
      </p>

      <div className="mb-4 rounded-xl border border-line bg-card p-5">
        <div className="mb-2 text-sm text-ink-soft">Site handmatig toevoegen (zonder wizard)</div>
        <form onSubmit={onCreate} className="flex gap-2">
          <input
            type="text"
            required
            placeholder={domainFocused ? '' : 'jouwbedrijf.nl'}
            className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-amber"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onFocus={() => setDomainFocused(true)}
            onBlur={() => setDomainFocused(false)}
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-lg bg-amber px-4 py-2.5 font-semibold text-[#0E2A3B] hover:opacity-90 disabled:opacity-50"
          >
            Toevoegen
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-line bg-card p-5">
        <div className="mb-2 text-sm text-ink-soft">Jouw sites</div>
        {list.isLoading ? (
          <p className="text-ink-soft">Laden…</p>
        ) : !list.data || list.data.length === 0 ? (
          <p className="text-ink-soft">Nog geen sites toegevoegd.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                <th className="border-b border-line pb-2">Naam</th>
                <th className="border-b border-line pb-2">Domein</th>
                <th className="border-b border-line pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((site) => (
                <tr key={site.id}>
                  <td className="border-b border-line py-2">
                    <Link to={`/sites/${site.id}`} className="text-ink hover:text-amber">
                      {site.name}
                    </Link>
                  </td>
                  <td className="border-b border-line py-2 text-ink-soft">{site.domain}</td>
                  <td className="border-b border-line py-2 text-right">
                    <span className="inline-flex items-center gap-3">
                      {site.run_estimate && (
                        <ConfirmDialog
                          trigger={
                            <button
                              type="button"
                              title="Meting starten"
                              aria-label={`Start meting voor ${site.name}`}
                              className="text-ink-soft hover:text-amber"
                            >
                              <PlayIcon />
                            </button>
                          }
                          title="Meting starten"
                          description={`Kost ~${site.run_estimate.estimated_credits} credits. Doorgaan?`}
                          confirmLabel="Starten"
                          busy={busy}
                          onConfirm={() => site.run_estimate && onRunNow(site.run_estimate.panel_id)}
                        />
                      )}
                      <Link
                        to={`/sites/${site.id}`}
                        title="Bekijken"
                        aria-label={`Bekijk ${site.name}`}
                        className="text-ink-soft hover:text-amber"
                      >
                        <ViewIcon />
                      </Link>
                      <Link
                        to={`/sites/${site.id}/setup`}
                        title="Bewerken"
                        aria-label={`Bewerk ${site.name}`}
                        className="text-ink-soft hover:text-amber"
                      >
                        <PencilIcon />
                      </Link>
                      <ConfirmDialog
                        trigger={
                          <button
                            type="button"
                            title="Verwijderen"
                            aria-label={`Verwijder ${site.name}`}
                            className="text-ink-soft hover:text-[#e08a90]"
                          >
                            <TrashIcon />
                          </button>
                        }
                        title="Site verwijderen"
                        description={`Weet je zeker dat je ${site.name} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`}
                        confirmLabel="Verwijderen"
                        destructive
                        onConfirm={() => onDelete(site.id)}
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AuthLayout>
  )
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3.5v17l15-8.5-15-8.5Z" />
    </svg>
  )
}

function ViewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  )
}
